// ./src/components/generic/TableDateTime/TableDateTime.tsx
//
// The ONE way the app shows a date + time in a list. A table cell renders a date line over a dimmed
// time line ("Sep 04, 2026" / "3:45 PM"); `inline` collapses it to a single dimmed line for mobile
// cards. Extracted from the Transactions table so every money / ticket / message list reads the same
// (same format, same nowrap, same helpers) — pass the row's timestamp (e.g. `paidAt || createdAt`).

import React from "react";
import { Box, Text } from "@mantine/core";

import { formatDate, formatDateTime } from "@/utils";

export interface TableDateTimeProps {
  /** ISO string (or Date). Renders `empty` when missing / invalid. */
  value?: string | Date | null;
  /** One combined line ("MMM D, YYYY @ h:mm A") for tight spots (mobile cards) instead of the stacked cell. */
  inline?: boolean;
  /** Placeholder shown when there's no value. */
  empty?: string;
}

export const TableDateTime: React.FC<TableDateTimeProps> = ({ value, inline = false, empty = "—" }) => {
  if (!value) {
    return (
      <Text size={inline ? "xs" : "md"} c="dimmed" style={{ whiteSpace: "nowrap" }}>
        {empty}
      </Text>
    );
  }

  if (inline) {
    return (
      <Text size="xs" c="dimmed" lineClamp={1}>
        {formatDateTime(value, "datetime")}
      </Text>
    );
  }

  return (
    <Box>
      <Text size="md" style={{ whiteSpace: "nowrap" }}>
        {formatDate(value)}
      </Text>
      <Text size="sm" c="dimmed" style={{ whiteSpace: "nowrap" }}>
        {formatDateTime(value, "time")}
      </Text>
    </Box>
  );
};
